import { Colors, Icon, Pills, ProgressBar, Text } from '@amelio/ui-library';

import {
    GROWTH_BLOCK_SURFACE,
    GROWTH_BLOCK_SURFACE_BLOCKERS,
    GROWTH_BLOCK_SURFACE_CONFIDENTIAL,
    GROWTH_CAPTION,
    GROWTH_PLACEHOLDER,
    GROWTH_QUESTION_TITLE,
} from './careerAspirationStyles';
import { isNewRolePath } from './careerAspirationValidation';
import { importanceLevelKey } from './importanceLevelLabel';

import type { CareerAspiration } from '@/types/career-growth';
import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { IMPORTANCE_LEVEL_MAX } from '@/types/career-growth';
import { getLocalizedText } from '@/utils/localization';

interface CareerAspirationAnswersProps {
    aspiration: CareerAspiration;
    employeeFirstName: string;
    dataTestId: string;
}

/**
 * The answers of one "Aspirations de carrière" meeting, read-only: the five blocks of the form,
 * followed by the confidentiality notice. Every question the path asks is rendered, blank answers
 * included, so a reader can tell "not asked" from "not answered".
 */
export const CareerAspirationAnswers = ({
    aspiration,
    employeeFirstName,
    dataTestId,
}: CareerAspirationAnswersProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    // The competency profile is asked on every path; the rest of the "new role" questions only on
    // the paths that aim at one -- the meeting never asks them otherwise, so showing them as
    // unanswered would read as a gap instead of a non-question.
    const showNewRoleAnswers = isNewRolePath(aspiration.developmentPath);
    const desiredProfileName = getLocalizedText(aspiration.desiredCompetencyProfileName ?? '');

    return (
        <>
            <AspirationBlock
                title={t('performanceProfile.growth.aspiration.developmentPath', {
                    name: employeeFirstName,
                })}
                dataTestId={`${dataTestId}-development-path`}
            >
                {aspiration.developmentPath ? (
                    <PillRow>
                        <Pills
                            variant={'navy'}
                            size={'sm'}
                        >
                            {t(
                                `performanceProfile.growth.aspiration.paths.${aspiration.developmentPath}`,
                            )}
                        </Pills>
                    </PillRow>
                ) : (
                    <NotAnswered />
                )}

                {aspiration.developmentPath && (
                    <div className={'grid gap-6 md:grid-cols-2'}>
                        {showNewRoleAnswers && (
                            <AspirationDetail
                                label={t('performanceProfile.growth.aspiration.positionTitle')}
                            >
                                <AnswerText text={aspiration.desiredPositionTitle} />
                            </AspirationDetail>
                        )}

                        <AspirationDetail
                            label={t('performanceProfile.growth.aspiration.competencyProfile')}
                        >
                            <AnswerText text={desiredProfileName} />
                        </AspirationDetail>
                    </div>
                )}

                {showNewRoleAnswers && (
                    <>
                        <ImportanceField
                            level={aspiration.importanceLevel}
                            employeeFirstName={employeeFirstName}
                            dataTestId={`${dataTestId}-importance`}
                        />

                        <AspirationField
                            label={t('performanceProfile.growth.aspiration.responsibilities')}
                        >
                            {aspiration.acceptsNewResponsibilities ? (
                                <PillRow>
                                    <Pills
                                        variant={'navy'}
                                        size={'sm'}
                                    >
                                        {t(
                                            `performanceProfile.growth.aspiration.responsibilityOptions.${aspiration.acceptsNewResponsibilities}`,
                                        )}
                                    </Pills>
                                </PillRow>
                            ) : (
                                <NotAnswered />
                            )}
                        </AspirationField>
                    </>
                )}
            </AspirationBlock>

            <AspirationBlock
                title={t('performanceProfile.growth.aspiration.alignment', {
                    name: employeeFirstName,
                })}
                dataTestId={`${dataTestId}-alignment`}
            >
                {aspiration.timeAlignmentNotes ? (
                    <QuotedNote text={aspiration.timeAlignmentNotes} />
                ) : (
                    <NotAnswered />
                )}

                {showNewRoleAnswers && (
                    <AspirationField label={t('performanceProfile.growth.aspiration.factors')}>
                        {aspiration.motivationFactors.length > 0 ? (
                            <PillRow>
                                {aspiration.motivationFactors.map((factor) => (
                                    <Pills
                                        key={factor}
                                        variant={'navy'}
                                        size={'sm'}
                                    >
                                        {t(
                                            `performanceProfile.growth.aspiration.factorOptions.${factor}`,
                                        )}
                                    </Pills>
                                ))}
                            </PillRow>
                        ) : (
                            <NotAnswered />
                        )}
                    </AspirationField>
                )}
            </AspirationBlock>

            <AspirationBlock
                title={t('performanceProfile.growth.aspiration.supportNeeds', {
                    name: employeeFirstName,
                })}
                dataTestId={`${dataTestId}-support-needs`}
            >
                {aspiration.supportNeeds.length > 0 || aspiration.supportNeedsOther ? (
                    <PillRow>
                        {aspiration.supportNeeds.map((need) => (
                            <Pills
                                key={need}
                                variant={'navy'}
                                size={'sm'}
                            >
                                {t(
                                    `performanceProfile.growth.aspiration.supportNeedOptions.${need}`,
                                    { managerLabel: getManagerLabel() },
                                )}
                            </Pills>
                        ))}
                        {aspiration.supportNeedsOther && (
                            <Pills
                                variant={'navy'}
                                size={'sm'}
                            >
                                {aspiration.supportNeedsOther}
                            </Pills>
                        )}
                    </PillRow>
                ) : (
                    <NotAnswered />
                )}
            </AspirationBlock>

            <AspirationBlock
                title={t('performanceProfile.growth.aspiration.blockers')}
                className={GROWTH_BLOCK_SURFACE_BLOCKERS}
                dataTestId={`${dataTestId}-blockers`}
            >
                {aspiration.growthBlockers.length > 0 ? (
                    <PillRow>
                        {aspiration.growthBlockers.map((blocker) => (
                            <BlockerPill key={blocker}>
                                {t(
                                    `performanceProfile.growth.aspiration.blockerOptions.${blocker}`,
                                )}
                            </BlockerPill>
                        ))}
                    </PillRow>
                ) : (
                    <NotAnswered />
                )}

                {aspiration.blockersNotes && <QuotedNote text={aspiration.blockersNotes} />}
            </AspirationBlock>

            <AspirationBlock
                title={t('performanceProfile.growth.aspiration.departureRisk', {
                    name: employeeFirstName,
                })}
                className={GROWTH_BLOCK_SURFACE_CONFIDENTIAL}
                dataTestId={`${dataTestId}-departure-risk`}
            >
                {aspiration.departureRisk ? (
                    <PillRow>
                        <Pills
                            variant={'navy'}
                            size={'md'}
                        >
                            {t(
                                `performanceProfile.growth.aspiration.departureRiskOptions.${aspiration.departureRisk}`,
                            )}
                        </Pills>
                    </PillRow>
                ) : (
                    <NotAnswered />
                )}
            </AspirationBlock>

            <div
                className={'flex flex-wrap items-center gap-2 px-1'}
                data-testid={`${dataTestId}-confidential`}
            >
                <Pills
                    variant={'default'}
                    size={'audience'}
                >
                    <Icon
                        name={'security-lock'}
                        size={12}
                        color={'inherit'}
                        ariaHidden
                    />
                    {t('performanceProfile.growth.aspiration.confidential')}
                </Pills>
                <p className={GROWTH_CAPTION}>
                    {t('performanceProfile.growth.aspiration.confidentialNote')}
                </p>
            </div>
        </>
    );
};

interface AspirationBlockProps {
    title: string;
    className?: string;
    dataTestId?: string;
    children: ReactNode;
}

/** One question of the meeting, on the shared panel. */
const AspirationBlock = ({
    title,
    className = GROWTH_BLOCK_SURFACE,
    dataTestId,
    children,
}: AspirationBlockProps) => (
    <div
        className={className}
        data-testid={dataTestId}
    >
        <h4 className={GROWTH_QUESTION_TITLE}>{title}</h4>
        {children}
    </div>
);

/** A follow-up question nested inside a block. */
const AspirationField = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className={'flex flex-col gap-3'}>
        <span className={'text-navy text-[14px] font-medium'}>{label}</span>
        {children}
    </div>
);

/** A named value of the "new role" sub-block, captioned rather than asked. */
const AspirationDetail = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className={'flex flex-col gap-1.5'}>
        <span className={`${GROWTH_CAPTION} tracking-wide uppercase`}>{label}</span>
        {children}
    </div>
);

const AnswerText = ({ text }: { text: string | null }) =>
    text?.trim() ? <Text color={Colors.NAVY}>{text}</Text> : <NotAnswered />;

const PillRow = ({ children }: { children: ReactNode }) => (
    <div className={'flex flex-wrap gap-2'}>{children}</div>
);

const QuotedNote = ({ text }: { text: string }) => (
    <Text
        color={Colors.NAVY}
        className={'leading-relaxed italic'}
    >{`“${text}”`}</Text>
);

/**
 * The blockers wear a warning tone. `Pills` has no variant for it, and its own `bg-*` utility is
 * emitted after the application's colour tokens, so overriding it from `className` would take an
 * `!important` the repository bans -- a span carrying the whole chip is the honest way to draw it.
 */
const BlockerPill = ({ children }: { children: ReactNode }) => (
    <span
        className={
            'bg-growth-blocker inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-medium text-white'
        }
    >
        {children}
    </span>
);

const NotAnswered = () => {
    const { t } = useAmelioTranslations();

    return (
        <span className={GROWTH_PLACEHOLDER}>
            {t('performanceProfile.growth.aspiration.notAnswered')}
        </span>
    );
};

interface ImportanceFieldProps {
    level: number | null;
    employeeFirstName: string;
    dataTestId: string;
}

/** "How important is it…": the level out of five, its word, and the matching gauge. */
const ImportanceField = ({ level, employeeFirstName, dataTestId }: ImportanceFieldProps) => {
    const { t } = useAmelioTranslations();

    const question = t('performanceProfile.growth.aspiration.importance', {
        name: employeeFirstName,
    });

    if (level === null) {
        return (
            <AspirationField label={question}>
                <NotAnswered />
            </AspirationField>
        );
    }

    const levelWordKey = importanceLevelKey(level);

    return (
        <AspirationField label={question}>
            <div className={'flex flex-wrap items-center gap-4'}>
                <Text
                    color={Colors.NAVY}
                    weight={'medium'}
                >
                    {`${t('performanceProfile.growth.aspiration.importanceOutOf', {
                        level,
                        max: IMPORTANCE_LEVEL_MAX,
                    })} — ${t(levelWordKey)}`}
                </Text>

                <ProgressBar
                    value={(level / IMPORTANCE_LEVEL_MAX) * 100}
                    size={'lg'}
                    className={'max-w-85 min-w-34 flex-1'}
                    ariaLabel={question}
                    dataTestId={dataTestId}
                />
            </div>
        </AspirationField>
    );
};
